import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX, FiClock, FiMapPin, FiChevronDown, FiCheck } from 'react-icons/fi';
import { FormLabel } from '../../ui/FormLabel';
import { Input } from '../../ui/Input';
import { DateInput } from '../../ui/DateInput';
import { TimezoneSelect } from '../../ui/TimezoneSelect';

interface ScheduleEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  mode: 'add' | 'edit';

  title: string;
  setTitle: (val: string) => void;

  startDate: string;
  setStartDate: (val: string) => void;

  startTime: string;
  setStartTime: (val: string) => void;

  endDate: string;
  setEndDate: (val: string) => void;

  endTime: string;
  setEndTime: (val: string) => void;

  timezone: string;
  setTimezone: (val: string) => void;

  location: string;
  setLocation: (val: string) => void;

  assigneeIds: string[];
  setAssigneeIds: (val: string[]) => void;
  membersData?: any;

  notes: string;
  setNotes: (val: string) => void;

  errors: Record<string, string | undefined>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>;
}

export const ScheduleEntryModal: React.FC<ScheduleEntryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  mode,
  title,
  setTitle,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  timezone,
  setTimezone,
  location,
  setLocation,
  assigneeIds,
  setAssigneeIds,
  membersData,
  notes,
  setNotes,
  errors,
  setErrors,
}) => {
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Focus the first field (Title) whenever the form opens, so mobile users get
  // the keyboard immediately instead of having to tap in manually. The short
  // delay lets the entrance animation mount before we focus/scroll to it.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.scrollIntoView({ block: 'nearest' });
    }, 100);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!assigneeDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target as Node)) {
        setAssigneeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [assigneeDropdownOpen]);

  const memberOptions = (membersData?.results ?? []).map((m: any) => {
    const memberUser = m.user || m.member || m;
    return {
      id: String(memberUser.id ?? m.id),
      name: memberUser.full_name || memberUser.username || memberUser.email || `Member ${m.id}`,
      role: m.role || 'Member',
    };
  });

  const toggleAssignee = (id: string) => {
    setAssigneeIds(
      assigneeIds.includes(id) ? assigneeIds.filter((x) => x !== id) : [...assigneeIds, id],
    );
  };

  const selectedAssigneeLabel =
    assigneeIds.length === 0
      ? 'Unassigned'
      : memberOptions
          .filter((m: { id: string; name: string }) => assigneeIds.includes(m.id))
          .map((m: { name: string }) => m.name)
          .join(', ') || `${assigneeIds.length} selected`;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-5 sm:p-6 text-left flex flex-col max-h-[82vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden"
          >
            {/* Sticky Header */}
            <div className="shrink-0 flex items-start justify-between pb-4 mb-4 border-b border-white/5 bg-[#0B0B0B] z-20">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {mode === 'add' ? 'Add Schedule Entry' : 'Edit Schedule Entry'}
                </h2>
                <p className="text-zinc-550 text-xs mt-1">
                  {mode === 'add'
                    ? 'Add an activity to the event-day schedule.'
                    : 'Update event-day schedule details.'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto no-scrollbar space-y-4 pr-1">
                {/* Title */}
                <div>
                  <FormLabel required className="text-zinc-400 text-xs font-semibold mb-2">
                    Title
                  </FormLabel>
                  <input
                    ref={titleInputRef}
                    type="text"
                    placeholder="e.g. Doors Open, Soundcheck, Headline Set"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
                    }}
                    className={`w-full bg-[#0F0F0F] border outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all ${
                      errors.title
                        ? 'border-red-500/80 focus:border-red-500'
                        : 'border-white/5 hover:border-white/10 focus:border-white/20'
                    }`}
                  />
                  {errors.title && <p className="text-red-400 text-xs mt-1">{errors.title}</p>}
                </div>

                {/* Start Date / Start Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <FormLabel required className="text-zinc-400 text-xs font-semibold mb-2">
                      Start Date
                    </FormLabel>
                    <DateInput
                      value={startDate}
                      onChange={(val) => {
                        setStartDate(val);
                        if (errors.startDateTime)
                          setErrors((prev) => ({ ...prev, startDateTime: undefined }));
                      }}
                      className={`text-xs rounded-xl px-4 py-3.5 h-auto ${
                        errors.startDateTime
                          ? 'border-red-500/80 focus:border-red-500'
                          : 'bg-[#0F0F0F] border-white/5 hover:border-white/10 focus:border-white/20'
                      }`}
                    />
                  </div>
                  <div>
                    <FormLabel required className="text-zinc-400 text-xs font-semibold mb-2">
                      Start Time
                    </FormLabel>
                    <Input
                      type="time"
                      icon={FiClock}
                      placeholder="Start time"
                      value={startTime}
                      onChange={(e) => {
                        setStartTime(e.target.value);
                        if (errors.startDateTime)
                          setErrors((prev) => ({ ...prev, startDateTime: undefined }));
                      }}
                      className={`text-xs rounded-xl pl-10 pr-4 py-3.5 h-auto scheme-dark ${
                        errors.startDateTime
                          ? 'border-red-500/80 focus:border-red-500'
                          : 'bg-[#0F0F0F] border-white/5 hover:border-white/10 focus:border-white/20'
                      }`}
                    />
                  </div>
                  {errors.startDateTime && (
                    <p className="text-red-400 text-xs -mt-2 sm:col-span-2">
                      {errors.startDateTime}
                    </p>
                  )}
                </div>

                {/* End Date / End Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <FormLabel required className="text-zinc-400 text-xs font-semibold mb-2">
                      End Date
                    </FormLabel>
                    <DateInput
                      value={endDate}
                      onChange={(val) => {
                        setEndDate(val);
                        if (errors.endDateTime)
                          setErrors((prev) => ({ ...prev, endDateTime: undefined }));
                      }}
                      className={`text-xs rounded-xl px-4 py-3.5 h-auto ${
                        errors.endDateTime
                          ? 'border-red-500/80 focus:border-red-500'
                          : 'bg-[#0F0F0F] border-white/5 hover:border-white/10 focus:border-white/20'
                      }`}
                    />
                  </div>
                  <div>
                    <FormLabel required className="text-zinc-400 text-xs font-semibold mb-2">
                      End Time
                    </FormLabel>
                    <Input
                      type="time"
                      icon={FiClock}
                      placeholder="End time"
                      value={endTime}
                      onChange={(e) => {
                        setEndTime(e.target.value);
                        if (errors.endDateTime)
                          setErrors((prev) => ({ ...prev, endDateTime: undefined }));
                      }}
                      className={`text-xs rounded-xl pl-10 pr-4 py-3.5 h-auto scheme-dark ${
                        errors.endDateTime
                          ? 'border-red-500/80 focus:border-red-500'
                          : 'bg-[#0F0F0F] border-white/5 hover:border-white/10 focus:border-white/20'
                      }`}
                    />
                  </div>
                  {errors.endDateTime && (
                    <p className="text-red-400 text-xs -mt-2 sm:col-span-2">{errors.endDateTime}</p>
                  )}
                </div>

                {/* Timezone */}
                <div>
                  <FormLabel required className="text-zinc-400 text-xs font-semibold mb-2">
                    Timezone
                  </FormLabel>
                  <TimezoneSelect
                    value={timezone}
                    onChange={(val) => {
                      setTimezone(val);
                      if (errors.timezone) setErrors((prev) => ({ ...prev, timezone: undefined }));
                    }}
                  />
                  {errors.timezone && (
                    <p className="text-red-400 text-xs mt-1">{errors.timezone}</p>
                  )}
                </div>

                {/* Location */}
                <div>
                  <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                    Location
                  </FormLabel>
                  <div className="relative">
                    <FiMapPin className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="e.g. Main Stage, VIP Area, Backstage"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-white/20 outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3.5 transition-all"
                    />
                  </div>
                </div>

                {/* Assigned To (multi-select) */}
                <div ref={assigneeDropdownRef}>
                  <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                    Assigned To
                  </FormLabel>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setAssigneeDropdownOpen((o) => !o)}
                      className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-white/20 outline-none text-white text-xs rounded-xl pl-4 pr-10 py-3.5 text-left cursor-pointer transition-all truncate"
                    >
                      <span className={assigneeIds.length === 0 ? 'text-zinc-400' : ''}>
                        {selectedAssigneeLabel}
                      </span>
                    </button>
                    <FiChevronDown
                      className={`w-4 h-4 text-zinc-500 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${assigneeDropdownOpen ? 'rotate-180' : ''}`}
                    />

                    {assigneeDropdownOpen && (
                      <div className="absolute z-30 mt-2 w-full rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden">
                        <ul className="max-h-56 overflow-y-auto py-1">
                          {memberOptions.length === 0 ? (
                            <li className="px-4 py-3 text-xs text-zinc-500">No members found</li>
                          ) : (
                            memberOptions.map((m: { id: string; name: string; role: string }) => {
                              const checked = assigneeIds.includes(m.id);
                              return (
                                <li
                                  key={m.id}
                                  onClick={() => toggleAssignee(m.id)}
                                  className={`flex items-center justify-between gap-2 px-4 py-2.5 text-xs cursor-pointer transition-colors ${
                                    checked
                                      ? 'bg-blue-600/20 text-white'
                                      : 'text-zinc-300 hover:bg-white/5'
                                  }`}
                                >
                                  <span className="truncate">
                                    {m.name} <span className="text-zinc-500">({m.role})</span>
                                  </span>
                                  <span
                                    className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                                      checked ? 'bg-blue-500 border-blue-500' : 'border-zinc-600'
                                    }`}
                                  >
                                    {checked && <FiCheck className="w-3 h-3 text-white" />}
                                  </span>
                                </li>
                              );
                            })
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <FormLabel optional className="text-zinc-400 text-xs font-semibold mb-2">
                    Notes (Internal)
                  </FormLabel>
                  <textarea
                    placeholder="What's this project about?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-white/20 outline-none text-white text-xs rounded-xl px-4 py-3.5 resize-none transition-all"
                  />
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="shrink-0 flex items-center justify-end pt-4 mt-4 border-t border-white/5 bg-[#0B0B0B] z-20">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto min-w-[200px] px-8 py-3 bg-white text-black hover:bg-zinc-100 rounded-full text-xs font-bold cursor-pointer transition-all active:scale-95 shadow-xl shadow-white/5 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Saving...' : mode === 'add' ? 'Add Entry' : 'Save Changes'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
